import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { authApi } from "../../api/auth";
import { useAuthStore } from "../../store/authStore";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Card } from "../../components/ui/Card";

export function VerifyEmailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setAuth = useAuthStore((s) => s.setAuth);

  const emailFromParams = searchParams.get("email") || "";
  const codeFromParams = searchParams.get("code") || "";
  const phoneFromParams = searchParams.get("phone") || "";

  const [email, setEmail] = useState(emailFromParams);
  const [code, setCode] = useState(codeFromParams);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const autoSubmitted = useRef(false);

  const handleVerify = async (otp: string, emailAddr: string) => {
    if (!emailAddr || !otp) return;
    setError(null);
    setLoading(true);
    try {
      const response = await authApi.verifyEmail(emailAddr, otp);
      if (!response.success) {
        setError(response.error || t("auth.verificationFailedError"));
        return;
      }
      if (response.phone_confirmation_required) {
        const phone = phoneFromParams || response.phone || "";
        const params = new URLSearchParams({ phone });
        navigate(`/verify-phone?${params.toString()}`, { replace: true });
        return;
      }
      if (response.token) {
        setAuth(response.token, response.user, response.organization, response.businesses, undefined, undefined);
        await useAuthStore.getState().resolveBusinessAndOutlet();
        navigate("/dashboard", { replace: true });
      } else if (response.mfa_required && response.mfa_token) {
        // Two-step is on (#159): straight to the code, not the password again.
        navigate("/login", { replace: true, state: { mfaToken: response.mfa_token } });
      } else {
        navigate("/login", {
          replace: true,
          state: { message: "Email verified! You can now sign in." },
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  // Auto-submit when both email and code come from URL params (magic link click)
  useEffect(() => {
    if (emailFromParams && codeFromParams && !autoSubmitted.current) {
      autoSubmitted.current = true;
      handleVerify(codeFromParams, emailFromParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResend = async () => {
    if (!email) {
      setError(t("auth.enterEmailFirstError"));
      return;
    }
    setResendMessage(null);
    setError(null);
    setResendLoading(true);
    try {
      const result = await authApi.resendOTP(email);
      setResendMessage(result.message || t("auth.newCodeSent"));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to resend code");
    } finally {
      setResendLoading(false);
    }
  };

  const isAutoVerifying = emailFromParams && codeFromParams && !error && loading;

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-alt p-4">
      <Card className="w-full max-w-md" title={t("auth.verifyYourEmail")}>
        {isAutoVerifying ? (
          <div className="text-center py-6">
            <p className="text-text-muted">{t("auth.verifyingYourEmail")}</p>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-text-muted">
              {t("auth.enterEmailOtpDescription")}
            </p>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
                {error}
              </div>
            )}

            {resendMessage && (
              <div className="p-3 bg-green-50 border border-green-200 text-green-700 rounded-md text-sm">
                {resendMessage}
              </div>
            )}

            <Input
              label={t("auth.email")}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("auth.emailPlaceholder")}
              disabled={!!emailFromParams}
            />

            <Input
              label={t("auth.verificationCode")}
              type="text"
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456"
              maxLength={6}
            />

            <Button
              type="button"
              className="w-full"
              loading={loading}
              onClick={() => handleVerify(code, email)}
              disabled={code.length !== 6 || !email}
            >
              {t("auth.verifyEmail")}
            </Button>

            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={handleResend}
                disabled={resendLoading}
                className="text-brand hover:underline disabled:opacity-50"
              >
                {resendLoading ? t("auth.sending") : t("auth.resendCode")}
              </button>
              <Link to="/login" className="text-text-muted hover:underline">
                {t("auth.backToLogin")}
              </Link>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
